import gsap from 'gsap';

const theme = window.ambidryTheme || {};
const plusSrc = theme.plusIcon || '';
const minusSrc = theme.minusIcon || '';
const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const mobileQuery = window.matchMedia('(max-width: 991px)');

export function initSpecialties() {
  const specialtyStage = document.querySelector('[data-specialty-stage]');
  if (!specialtyStage) return;

  const cards = {
    lemon: specialtyStage.querySelector('[data-specialty="lemon"]'),
    beta: specialtyStage.querySelector('[data-specialty="beta"]'),
  };
  const panels = {
    lemon: specialtyStage.querySelector('[data-specialty-panel="lemon"]'),
    beta: specialtyStage.querySelector('[data-specialty-panel="beta"]'),
  };

  let active = specialtyStage.dataset.active || 'lemon';
  let animating = false;
  let mobileTl = null;

  const isMobile = () => mobileQuery.matches;

  const revealCards = () => {
    ['lemon', 'beta'].forEach((id) => {
      const card = cards[id];
      if (card) gsap.set(card, { opacity: 1, scale: 1, x: 0, y: 0 });
    });
  };

  const clearInlineLayout = () => {
    ['lemon', 'beta'].forEach((id) => {
      const card = cards[id];
      if (!card) return;
      gsap.set(card, {
        clearProps:
          'position,left,top,width,height,zIndex,clipPath,webkitClipPath,margin,transform,scale,x,y,overflow',
        opacity: 1,
      });
      const media = card.querySelector('.specialty-card__image');
      if (media) gsap.set(media, { clearProps: 'width,minWidth,height,maxWidth' });
    });
    gsap.set(specialtyStage, { clearProps: 'minHeight' });
  };

  const syncCardChrome = (id, isExpanded) => {
    const card = cards[id];
    if (!card) return;
    card.classList.toggle('is-expanded', isExpanded);
    card.classList.toggle('is-collapsed', !isExpanded);
    const btn = card.querySelector('.specialty-card__tag');
    const icon = btn?.querySelector('img');
    if (btn) btn.setAttribute('aria-expanded', String(isExpanded));
    if (icon) icon.src = isExpanded ? minusSrc : plusSrc;
  };

  const setPanelVisible = (id, on, opacity) => {
    const panel = panels[id];
    if (!panel) return;
    panel.classList.toggle('is-active', on);
    panel.setAttribute('aria-hidden', String(!on));
    panel.style.visibility = on ? 'visible' : 'hidden';
    panel.style.opacity = String(opacity);
    if (on) panel.querySelector('.line-decor')?.classList.add('is-animated');
  };

  const setActiveState = (state) => {
    ['lemon', 'beta'].forEach((id) => syncCardChrome(id, id === state));
    setPanelVisible('lemon', state === 'lemon', state === 'lemon' ? 1 : 0);
    setPanelVisible('beta', state === 'beta', state === 'beta' ? 1 : 0);
    specialtyStage.dataset.active = state;
    active = state;
  };

  const applyLayoutInstant = (state) => {
    if (mobileTl) {
      mobileTl.kill();
      mobileTl = null;
    }
    setActiveState(state);
    clearInlineLayout();
    revealCards();
    animating = false;
  };

  applyLayoutInstant(active);

  const lockCardWidth = (card, boxWidth, mediaWidth, mediaHeight, zIndex) => {
    gsap.set(card, {
      width: boxWidth,
      overflow: 'hidden',
      zIndex,
      opacity: 1,
      scale: 1,
      x: 0,
      y: 0,
    });
    const media = card.querySelector('.specialty-card__image');
    if (media) {
      gsap.set(media, {
        width: mediaWidth,
        minWidth: mediaWidth,
        height: mediaHeight,
        maxWidth: 'none',
      });
    }
  };

  const switchMobile = (next) => {
    const prev = active;
    const incoming = cards[next];
    const outgoing = cards[prev];
    const outPanel = panels[prev];
    const inPanel = panels[next];
    if (!incoming || !outgoing) {
      applyLayoutInstant(next);
      return;
    }

    animating = true;
    gsap.killTweensOf([incoming, outgoing, outPanel, inPanel].filter(Boolean));

    const outRect = outgoing.getBoundingClientRect();
    const inRect = incoming.getBoundingClientRect();
    const expandedW = outRect.width;
    const peekW = inRect.width;
    const expandedH = outRect.height;

    lockCardWidth(incoming, peekW, expandedW, expandedH, 4);
    lockCardWidth(outgoing, expandedW, expandedW, expandedH, 3);

    syncCardChrome('lemon', next === 'lemon');
    syncCardChrome('beta', next === 'beta');
    specialtyStage.dataset.active = next;
    active = next;
    if (inPanel) setPanelVisible(next, true, 0);

    mobileTl = gsap.timeline({
      defaults: { ease: 'power3.inOut' },
      onComplete: () => {
        clearInlineLayout();
        revealCards();
        mobileTl = null;
        animating = false;
      },
    });

    mobileTl.to(incoming, { width: expandedW, duration: 0.62 }, 0);
    mobileTl.to(outgoing, { width: peekW, duration: 0.62 }, 0);

    if (outPanel) {
      mobileTl.to(
        outPanel,
        {
          opacity: 0,
          duration: 0.2,
          ease: 'power1.out',
          onComplete: () => setPanelVisible(prev, false, 0),
        },
        0
      );
    }

    if (inPanel) {
      mobileTl.to(
        inPanel,
        {
          opacity: 1,
          duration: 0.35,
          ease: 'power1.out',
        },
        0.18
      );
    }
  };

  const switchTo = (next) => {
    if (animating || next === active) return;
    if (!cards.lemon || !cards.beta) return;

    if (prefersReduced || !gsap) {
      applyLayoutInstant(next);
      return;
    }

    if (isMobile()) {
      switchMobile(next);
      return;
    }

    animating = true;
    const prev = active;
    const outPanel = panels[prev];
    const inPanel = panels[next];
    const tl = gsap.timeline({
      defaults: { ease: 'power2.inOut' },
      onComplete: () => {
        animating = false;
      },
    });

    if (outPanel) {
      tl.to(outPanel, {
        opacity: 0,
        duration: 0.15,
        onComplete: () => setPanelVisible(prev, false, 0),
      });
    }

    tl.call(
      () => {
        syncCardChrome('lemon', next === 'lemon');
        syncCardChrome('beta', next === 'beta');
        specialtyStage.dataset.active = next;
        active = next;
        if (inPanel) setPanelVisible(next, true, 0);
      },
      null,
      outPanel ? 0.12 : 0
    );

    if (inPanel) {
      tl.to(inPanel, {
        opacity: 1,
        duration: 0.35,
        ease: 'power1.out',
      });
    }
  };

  specialtyStage.addEventListener('click', (event) => {
    const btn = event.target.closest('.specialty-card__tag');
    if (!btn || !specialtyStage.contains(btn)) return;
    event.preventDefault();
    switchTo(active === 'lemon' ? 'beta' : 'lemon');
  });

  window.addEventListener('resize', () => applyLayoutInstant(active));
}
